import { useEffect, type RefObject } from 'react';

/** Into view by the shortest way, and not moved at all where it is in view already. */
const bringIntoView = (element: HTMLElement | null | undefined): void =>
  element?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });

/**
 * Scrolls a part of the canvas into view when it is pointed at from outside: from the
 * sections list, or from the line of an issue. `turn` is nothing for a part that is not the
 * one pointed at, and a number that rises with each pointing for the one that is, so that
 * pointing at the same part again scrolls to it again. The canvas hands a turn to the first
 * part that stands for what was pointed at and to none after it (`DesignCanvas`): a pointing
 * is answered once, and a part that is drawn for the same target later is not scrolled to.
 *
 * A click on the canvas itself points at nothing: what was clicked is already in view. The
 * one press there that does point is a step in a block's toolbar. The block stands somewhere
 * else after it, a place along its page or on another sheet, and whoever moved it, with the
 * mouse or from the keyboard, is to find it in view with its toolbar.
 */
export const useReveal = (node: RefObject<HTMLElement | null>, turn: number | undefined): void => {
  useEffect(() => {
    if (turn) bringIntoView(node.current);
  }, [node, turn]);
};

/**
 * What is handed the keyboard's focus after a press that takes the pressed button away. After
 * a press in a block's toolbar it is a block or a page, named as the editor names them, and
 * `step` is for a block that was moved a place: which of the two buttons moved it. After a
 * press on one of the inspector's own buttons it is a part of the inspector, named as the
 * inspector marks it: its `title`, or its `duplicate` button.
 */
export interface Handover {
  kind: 'block' | 'page' | 'inspector';
  id: string;
  step?: 'up' | 'down';
}

/**
 * The element that stands for a block or a page on the canvas, or for a part of the
 * inspector: a block's frame, a page's name and the inspector's parts say which they are in
 * `data-block`, `data-page` and `data-inspector`. The id is compared and not written into a
 * selector: it is whatever text the API took for one.
 */
const partOf = ({ kind, id }: Handover): HTMLElement | undefined =>
  Array.from(document.querySelectorAll<HTMLElement>(`[data-${kind}]`)).find((element) => element.dataset[kind] === id);

/** Whether the browser shows the focus on the element that has it: it does for someone at the keyboard, and not after a click. */
const showsFocus = (element: HTMLElement): boolean => {
  try {
    return element.matches(':focus-visible');
  } catch {
    // A browser that does not know the selector refuses it.
    return false;
  }
};

/**
 * Puts the keyboard's focus back after a press that took the pressed button away: on the
 * canvas after a press in a block's toolbar, in the inspector after a press on one of the
 * inspector's own buttons.
 *
 * A press in a block's toolbar can take away the button that was pressed, and the browser
 * then leaves the focus on nothing: a block that goes to another page is drawn anew there,
 * one that has reached an end of the design has that button switched off, a copy takes the
 * selection and the toolbar with it, a deleted block is gone. Only a block that changes
 * places on its page keeps its button and the focus on it: where React takes its frame out
 * and puts it back in, React gives the focus back by itself. So the editor names what is to
 * have the focus: a block that was moved, on the button that moved it, to be pressed again,
 * or on its frame where that button is switched off; the copy of a block, and the block
 * beside a deleted one, on its frame; a page, on its name.
 *
 * The inspector is drawn anew whenever something else is selected, and its Duplicate and
 * its two Delete buttons bring that about: the copy is selected, the block or the page is
 * gone. Whoever pressed one is working in the inspector and is kept there, with every sheet
 * of the canvas between the two in Tab order: the focus goes to the same Duplicate button,
 * drawn for the copy, to be pressed again, and after a deletion to the title of what the
 * inspector shows next, as it does where the copy has filled its page and Duplicate is
 * switched off. The title and not the first field under it: a field that takes the focus
 * shows it after a click as well, and takes whatever is typed next.
 *
 * Whoever pressed with the mouse is to notice none of it: taking the focus scrolls nothing,
 * and the browser draws no ring around a focus that follows a click. Where it does show the
 * focus, for someone at the keyboard, what has it is brought into view as well, as the
 * browser does for a focus that Tab moves.
 *
 * It answers a press and nothing else: `handed` is a new object for each, so a frame that is
 * drawn anew later, after a drop on another page for one, takes no focus, and neither does
 * an inspector that is drawn anew for a click on the canvas. A block carried with the
 * keyboard is left to the drag context, which puts the focus back on its grip.
 */
export const useHandover = (handed: Handover | null): void => {
  useEffect(() => {
    if (!handed) return;

    const part = partOf(handed);
    const button = handed.step ? part?.querySelector<HTMLElement>(`[data-step="${handed.step}"]:not(:disabled)`) : null;
    const taker = button ?? part;

    if (!taker) return;

    taker.focus({ preventScroll: true });
    if (showsFocus(taker)) bringIntoView(part);
  }, [handed]);
};
